import React from "react";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { CategoryCountDto } from "@swagger/typescript-fetch-goflyer";
import { BRAND_COLOR } from "./constants";
import { getCategoryTooltip } from "./helpers";

interface CategoryCardProps {
  category: CategoryCountDto;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({ category }) => {
  const { t } = useTranslation();
  const hasFrontPage = category.frontPageCount > 0;
  const tooltipText = getCategoryTooltip(category, t);

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Paper
        elevation={0}
        sx={{
          p: 1.75,
          height: "100%",
          border: "1px solid",
          borderColor: hasFrontPage ? BRAND_COLOR : "#e8e8e8",
          borderRadius: 1.5,
          backgroundColor: "#fff",
          transition: "all 0.15s ease",
          "&:hover": {
            borderColor: BRAND_COLOR,
            boxShadow: "0 2px 8px rgba(255,128,56,0.1)",
          },
        }}
        data-testid="statistics-category-item"
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            mb: 1.5,
            color: "#1a1a1a",
            fontSize: "0.875rem",
          }}
          data-testid="statistics-category-item-name"
        >
          {category.name}
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Total */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", display: "block", mb: 0.25 }}
            >
              {t(messages.Total())}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: BRAND_COLOR, fontWeight: 600, fontSize: "1.25rem" }}
              data-testid="statistics-category-item-count"
            >
              {category.count}
            </Typography>
          </Box>

          {/* Front Page */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: hasFrontPage ? "#fff5f0" : "#f9f9f9",
              borderRadius: 1,
              p: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: hasFrontPage ? BRAND_COLOR : "text.secondary",
                fontSize: "0.7rem",
                display: "block",
                mb: 0.25,
              }}
            >
              {t(messages.Front_Page())}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: hasFrontPage ? BRAND_COLOR : "#999",
                fontWeight: hasFrontPage ? 600 : 400,
                fontSize: "1.25rem",
              }}
              data-testid="statistics-category-item-front-page-count"
            >
              {category.frontPageCount || 0}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Tooltip>
  );
};
